import { useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import { Loader2, Link2, Unlink } from 'lucide-react';

export default function OrgAllocationPanel() {
  const [orgs, setOrgs] = useState([]);
  const [customPlans, setCustomPlans] = useState([]);
  const [globalPlans, setGlobalPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selection, setSelection] = useState({}); // account_id -> plan_id draft
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [o, cfg] = await Promise.all([
        api.get('/super-admin/subscription/orgs'),
        api.get('/super-admin/subscription/pricing'),
      ]);
      setOrgs(o.data?.orgs || []);
      setGlobalPlans(cfg.data?.plans || []);
      setCustomPlans(cfg.data?.custom_plans || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load orgs'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const planOptions = useMemo(() => {
    // Global plans directly allocatable by SA: free (unassign), starter, growth.
    const allocatable = new Set(['free', 'starter', 'growth']);
    const globals = (globalPlans || [])
      .filter((p) => allocatable.has(p.id))
      .map((p) => ({ id: p.id, label: p.id === 'free' ? `${p.label} (unassign)` : `${p.label} (global)` }));
    return [
      ...globals,
      ...customPlans.map((p) => ({ id: p.id, label: `${p.label} (custom)`, disabled: !p.active })),
    ];
  }, [globalPlans, customPlans]);

  const labelFor = (planId) => planOptions.find((p) => p.id === planId)?.label || planId;

  const filtered = orgs.filter((o) => (o.name || '').toLowerCase().includes(q.toLowerCase()) || o.account_id.includes(q));

  const assign = async (accountId) => {
    const planId = selection[accountId];
    if (!planId) { toast.error('Pick a plan first'); return; }
    setBusy(accountId);
    try {
      await api.post('/super-admin/subscription/allocate', { account_id: accountId, plan_id: planId });
      toast.success(`Assigned ${labelFor(planId)}`);
      await load();
      setSelection((s) => ({ ...s, [accountId]: undefined }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Assignment failed'); }
    finally { setBusy(null); }
  };

  const unassign = async (accountId) => {
    setBusy(accountId);
    try {
      await api.post('/super-admin/subscription/deallocate', { account_id: accountId });
      toast.success('Org moved back to Free');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Unassign failed'); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="org-allocation-panel">
      <p className="text-sm text-slate-600 mb-4 max-w-2xl">
        Assign any plan — including custom plans — to a specific organization. Assigning a custom plan
        that bypasses Stripe activates it immediately (managed billing). To remove a plan, click Unassign
        (moves the org back to Free).
      </p>
      <Input placeholder="Search organizations…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none mb-4 max-w-sm" data-testid="org-alloc-search" />

      <div className="border border-slate-200 divide-y divide-slate-100 bg-white max-h-[520px] overflow-y-auto">
        {filtered.map((o) => {
          const isCustom = (o.current_plan_id || '').startsWith('custom_');
          return (
            <div key={o.account_id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`org-alloc-row-${o.account_id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{o.name}</div>
                <div className="text-xs text-slate-400 flex items-center gap-2">
                  current:
                  <Badge variant="outline" className={`rounded-none text-[10px] ${isCustom ? 'text-violet-600 border-violet-300' : 'text-slate-500'}`}>
                    {labelFor(o.current_plan_id)}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={selection[o.account_id] || ''} onValueChange={(v) => setSelection((s) => ({ ...s, [o.account_id]: v }))}>
                  <SelectTrigger className="rounded-none w-56 h-9 text-xs" data-testid={`org-alloc-select-${o.account_id}`}><SelectValue placeholder="Choose plan…" /></SelectTrigger>
                  <SelectContent>
                    {planOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id} disabled={p.disabled} data-testid={`org-alloc-opt-${o.account_id}-${p.id}`}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="rounded-none" disabled={busy === o.account_id} onClick={() => assign(o.account_id)} data-testid={`org-alloc-assign-${o.account_id}`}>
                  {busy === o.account_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                </Button>
                {(isCustom || o.current_plan_id !== 'free') && (
                  <Button size="sm" variant="outline" className="rounded-none text-rose-600" disabled={busy === o.account_id} onClick={() => unassign(o.account_id)} data-testid={`org-alloc-unassign-${o.account_id}`}>
                    <Unlink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-400">No organizations match.</div>}
      </div>
    </div>
  );
}
